/**
 * assetLibrary — la BIBLIOTHÈQUE de la boutique vue du builder (feature 0070).
 *
 * La demande de la fondatrice (05/10/2026) : « chaque image uploadée dans le store doit rester dans le
 * store, et si je veux uploader une image dans le builder, la première popup que je vois c'est cette bib
 * des images déjà uploadées — pas à chaque fois re-télécharger. »
 *
 * Jusqu'ici le backend ne savait PAS répondre à ça : il déposait des octets et rendait une URL, sans
 * jamais écrire qu'un envoi avait eu lieu. `store_asset` est le registre qui le permet, et ce module en
 * est la seule porte côté builder.
 *
 * Tenancy : aucun `store_id` ne part d'ici — le backend le lit dans le JWT (build-plan §10.1).
 */
import { apiRequest } from './http';

/** Une entrée telle que `AssetLibraryItemResponse` l'envoie. */
export interface LibraryAsset {
  id: string;
  /** L'URL de livraison — ce que le bloc stocke tel quel, sans transformation. */
  url: string;
  kind: 'IMAGE' | 'VIDEO';
  /**
   * Le nom du fichier sur le disque de la vendeuse. VIDE pour les entrées rattrapées par la reprise :
   * ce nom n'a jamais été stocké avant 0070 et rien ne peut l'inventer. L'écran doit donc savoir
   * afficher une vignette sans nom, pas supposer qu'il y en a un.
   */
  fileName: string;
  contentType: string;
  width: number | null;
  height: number | null;
  byteSize: number;
  alt: string | null;
  createdAt: string;
}

export interface LibraryPage {
  items: LibraryAsset[];
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

export interface LibraryQuery {
  kind?: 'IMAGE' | 'VIDEO';
  /** Un morceau de nom de fichier. */
  q?: string;
  page?: number;
}

/**
 * Une page de la bibliothèque, les plus RÉCENTES d'abord — ce que la vendeuse vient d'envoyer est ce
 * qu'elle cherche, donc l'ordre n'est pas un réglage de l'écran.
 */
export function fetchLibrary(query: LibraryQuery = {}): Promise<LibraryPage> {
  const params: Record<string, string> = {};
  if (query.kind) params.kind = query.kind;
  const needle = query.q?.trim();
  if (needle) params.q = needle;
  if (query.page) params.page = String(query.page);
  return apiRequest<LibraryPage>('/api/assets/library', { query: params });
}

/**
 * Retire une entrée de la LISTE. Les octets restent servis : cette URL peut être citée par une page
 * DÉJÀ PUBLIÉE, et une boutique en ligne qui perd ses photos parce que quelqu'un a rangé sa
 * bibliothèque est un dégât qu'on ne rattrape pas. C'est l'effacement doux du backend, pas une purge.
 */
export function removeFromLibrary(id: string): Promise<void> {
  return apiRequest<void>(`/api/assets/library/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * Le texte alternatif saisi une fois voyage AVEC l'image : réutilisée ailleurs, elle le repropose au
 * lieu de le faire ressaisir à chaque bloc.
 */
export function describeLibraryAsset(id: string, alt: string | null): Promise<LibraryAsset> {
  return apiRequest<LibraryAsset>(`/api/assets/library/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { alt },
  });
}
